import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { commonPostPutApi } from "../../../redux/reducers/commonApiSlice";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import CloseSubmitBtn from "../../common-components/custom-comps/CloseSubmitBtn";
import MultiSelect from "../../components-react/MultiSelect";
import { selectGymData } from "../../../redux/reducers/gymDataSlice";
import { fetchGymData } from "../../../data/APIs";
import "../../../styles/trigger-system/multi-select-dropdown.css";
import SelectDropdown from "../../components-react/SelectDropdown";
import { fetchAllMembersData } from "../../../redux/reducers/dropdownApiSlice";
import RecipientFilter from "../../common-components/RecipientFilter";
import {
  getSelectedUserUIDs,
  mergeRecipientFilterDefaults,
} from "../../common-components/RecipientFilterUtils";

const SubscriberForm = ({
  isEdit,
  rowData,
  closeModal,
  userInfoData,
  callFunction,
}) => {
  const dispatch = useDispatch();
  const { postPutLoading } = useSelector((state) => state.common);
  const { members } = useSelector((state) => state.dropdown);
  const [lists, setLists] = useState([]);
  const { sendyLists } = useSelector((state) => state.blog);
  const [recipientFilterErrors, setRecipientFilterErrors] = useState({});

  const forminitials = mergeRecipientFilterDefaults({
    selectedUsers: [],
    selectedLists: [],
    userType: isEdit ? rowData?.user_type : "paid",
  });
  const [formValues, setFormValues] = useState(forminitials);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);

  const userTypeOptions = [
    { label: "Paid", value: "paid" },
    { label: "Unpaid", value: "unpaid" },
  ];

  // Fetch available lists
  const fetchLists = async () => {
    try {
      const result = await dispatch(
        commonPostPutApi({
          method: "GET",
          endpoint: "/listmonk/lists",
          payload: {},
          token: userInfoData?.token,
        })
      );

      if (result?.payload?.status && result?.payload?.data) {
        const listOptions = result.payload.data.map((list) => ({
          label: list.name,
          value: list.id,
          info: list,
        }));
        setLists(listOptions);
      }
    } catch (error) {
      console.error("Error fetching lists:", error);
    }
  };

  const onChangeDropdown = (value, name) => {
    setIsSubmit(false);

    // For userType, extract the value from the option object
    if (name === "userType") {
      console.log("UserType dropdown changed:", value);
      const extractedValue = value?.value || value;
      console.log("Extracted value:", extractedValue);
      setFormValues({ ...formValues, [name]: extractedValue });
      return;
    }

    if (name === "selectedRecipient") {
      setFormValues((prev) => ({
        ...prev,
        selectedMembers: [],
        selectedTrainers: [],
        selectedReceptionists: [],
        selectedFollowUpMembers: [],
        selectedMemberType: [],
        selectedUsers: [],
        [name]: value,
      }));
    } else if (
      name === "selectedGym" ||
      name === "selectedSubscriptionType" ||
      name === "subscriptionTypeValue" ||
      name === "selectedPlanDuration" ||
      name === "selectedLeadSource" ||
      name === "selectedMemberType"
    ) {
      setFormValues((prev) => ({
        ...prev,
        selectedMembers: [],
        selectedTrainers: [],
        selectedReceptionists: [],
        selectedFollowUpMembers: [],
        selectedUsers: [],
        [name]: value,
      }));
    } else {
      setFormValues((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validateSubscriberForm = (values) => {
    const errors = {};

    // Keep the old validation for backward compatibility when no recipient is selected
    if (
      !values.selectedRecipient &&
      (!values.selectedUsers || values.selectedUsers.length === 0)
    ) {
      errors.selectedUsers = "At least one user is required!";
    }

    if (!values.selectedLists || values.selectedLists.length === 0) {
      errors.selectedLists = "At least one list is required!";
    }

    if (!values.userType) {
      errors.userType = "User type is required!";
    }

    return errors;
  };

  const handleSubmitForm = async (payload) => {
    console.log("handleSubmitForm called with payload:", payload);
    try {
      const method = "POST";
      const endpoint = "/listmonk/list/subscribe/add";
      const token = userInfoData?.token || "";

      console.log("Making API call:", { method, endpoint, payload, token });

      const result = await dispatch(
        commonPostPutApi({ method, endpoint, payload, token })
      );

      console.log("API result:", result);

      if (result?.payload?.status) {
        setIsSubmit(false);
        setFormValues(forminitials);

        callFunction();
        closeModal();
        toast.success(
          result?.payload?.message || "Subscribers added successfully!"
        );
      } else {
        const errorMessage =
          result?.payload?.data ||
          result?.payload?.message ||
          result?.error?.message;
        console.error("API error:", errorMessage);
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("Error processing subscribers:", error);
      toast.error("Failed to process subscribers");
    }
  };

  const onSumitForm = (e) => {
    e.preventDefault();
    console.log("Form submitted with values:", formValues);
    const errors = validateSubscriberForm(formValues);
    console.log("Validation errors:", errors);
    setFormErrors(errors);
    setIsSubmit(true);
  };

  useEffect(() => {
    dispatch(fetchGymData(userInfoData?.token, 1, 100, "", ""));
    fetchLists();
  }, []);

  useEffect(() => {
    console.log(
      "useEffect triggered - isSubmit:",
      isSubmit,
      "formErrors:",
      formErrors,
      "recipientFilterErrors:",
      recipientFilterErrors
    );

    const allErrors = { ...formErrors, ...recipientFilterErrors };

    if (isSubmit && Object.keys(allErrors)?.length === 0) {
      // Get user UIDs using the utility function
      const user_uids = getSelectedUserUIDs(formValues);

      const userData = {
        user_uids,
        target_list_ids: formValues.selectedLists.map((list) => list.value),
        user_type: formValues.userType,
        recipient_type: formValues.selectedRecipient?.value || "member",
        is_all_users: formValues.isAllMembers || false,
      };

      console.log("Submitting userData:", userData);

      const handleConfirm = async () => {
        handleSubmitForm(userData);
      };

      showConfirmAlert(
        handleConfirm,
        "Add Subscribers to Lists",
        "Edit Form",
        "Submit Form"
      );
    }
  }, [isSubmit, formErrors, recipientFilterErrors]);

  const listOptions = sendyLists?.data?.results?.map((list) => ({
    label: list.name,
    value: list.id,
    info: list,
  }));

  return (
    <div className="form-container">
      <form className="formContainer">
        {/* Recipient Filtering Section */}
        <RecipientFilter
          formValues={formValues}
          setFormValues={setFormValues}
          formErrors={recipientFilterErrors}
          userInfoData={userInfoData}
          onValidationChange={setRecipientFilterErrors}
          config={{
            showRecipientType: true,
            showMemberType: true,
            showGymFilter: true,
            showLeadSource: true,
            showSubscriptionFilters: true,
            showDateFilters: true,
            showSendToAll: true,
            recipientLabel: "recipient",
            required: true,
          }}
        />

        <div className="grayLine"></div>

        {/* List and User Type Selection */}
        <div className="formInputsGrid twoGridSystem">
          {/* Fallback for direct user selection (when no recipient filter is used) */}
          {!formValues.selectedRecipient && (
            <div className={`formInputBox span2`}>
              <label htmlFor="selectedUsers" className={`formInputLabel`}>
                Select Users <span className="required">*</span>
              </label>

              <div
                className={`commonWrapper no-border multi-select multi-show-option ${
                  formErrors?.selectedUsers ? "error" : ""
                }`}
              >
                <MultiSelect
                  options={members?.length > 0 ? members : []}
                  state={formValues.selectedUsers}
                  setState={(selectedValue) =>
                    onChangeDropdown(selectedValue, "selectedUsers")
                  }
                  name="select multiple users"
                  preventKeyboardOpen={false}
                  isShowList={true}
                />
              </div>
            </div>
          )}

          <div className={`formInputBox span2`}>
            <label htmlFor="selectedLists" className={`formInputLabel`}>
              Select Lists <span className="required">*</span>
            </label>

            <div
              className={`commonWrapper no-border multi-select multi-show-option ${
                formErrors?.selectedLists ? "error" : ""
              }`}
            >
              <MultiSelect
                options={listOptions}
                state={formValues.selectedLists}
                setState={(selectedValue) =>
                  onChangeDropdown(selectedValue, "selectedLists")
                }
                name="select multiple lists"
                preventKeyboardOpen={false}
                isShowList={true}
              />
            </div>
          </div>

          <div className="formInputBox">
            <label htmlFor="userType" className={`formInputLabel`}>
              User Type <span className="required">*</span>
            </label>

            <div
              className={`commonWrapper ${
                formErrors?.userType ? "formError" : ""
              }`}
            >
              <SelectDropdown
                options={userTypeOptions}
                selectedOption={
                  formValues.userType
                    ? userTypeOptions.find(
                        (opt) => opt.value === formValues.userType
                      )
                    : null
                }
                setSelectedOption={(selectedValue) =>
                  onChangeDropdown(selectedValue, "userType")
                }
                name="User Type"
              />
            </div>
          </div>
        </div>

        <CloseSubmitBtn
          closeLabel="Cancel"
          submitLabel="Add Subscribers"
          onClose={closeModal}
          onSubmit={onSumitForm}
          isLoading={postPutLoading}
        />
      </form>
    </div>
  );
};

export default SubscriberForm;
